import { useState } from "react";
import { PiWarningFill } from "react-icons/pi";
import { Button } from "../../webapp/ui";
import { isDev } from "../../globals";

export type SessionType = {
  id: string;
  last_seen?: string | null;
  is_mobile: boolean;
};

export const SessionView = ({
  currentSession,
  userId,
  activeSession: session,
  onLogout,
  onStartSession,
}: {
  currentSession?: { session_id: string; session_start: string } | undefined;
  userId: string;
  activeSession?: SessionType | null;
  onLogout: () => void;
  onStartSession: () => void;
}) => {
  const deviceType = session?.is_mobile ? "mobile" : "desktop";
  const [showSessionDetails, setShowSessionDetails] = useState(false);

  return (
    <div className="fixed inset-0 w-full h-full bg-background flex flex-col justify-center items-center">
      <div className="flex flex-col gap-[12px] items-center justify-center max-w-[500px] px-4">
        <div className="size-11 text-[#FFC942]">
          <PiWarningFill className="w-full h-full" />
        </div>

        <div className="flex flex-col gap-[18px] items-center justify-center">
          <h2 className="text-[18px] font-bold leading-6 text-foreground">Active session limit reached</h2>

          <div className="flex flex-col gap-3 items-center justify-center text-center">
            <p className="text-[13.5px] leading-[21px] text-foreground">
              You have reached your limit of 1 active {deviceType} session on Artis.
            </p>
            <div className="flex flex-col gap-[1px] items-center justify-center w-full">
              <p className="text-[13.5px] leading-[21px] text-foreground text-center">
                Continuing will log you out from your other {deviceType} session.
              </p>
              {!showSessionDetails && (
                <Button variant="text" color="neutral" onClick={() => setShowSessionDetails(true)}>
                  View Session Details
                </Button>
              )}
            </div>

            {showSessionDetails && session && (
              <div className="flex flex-col gap-3 items-center justify-center w-full text-left">
                <div className="border border-outline rounded-lg px-3 py-1.5 w-full text-left flex flex-col gap-[6px]">
                  <div className="flex flex-col gap-[3px]">
                    <p className="text-[13.5px] font-semibold text-foreground">Session ID: {session.id}</p>
                    <p className="text-[10.5px] font-medium text-foreground-muted capitalize">{deviceType}</p>
                  </div>
                  {session.last_seen && (
                    <p className="text-xs font-medium text-foreground-muted">Last seen: {session.last_seen}</p>
                  )}
                </div>

                {isDev && (
                  <div className="bg-card-muted border border-outline rounded-lg px-3 py-1.5 w-full">
                    <pre className="text-xs text-foreground whitespace-pre-wrap break-all">
                      {JSON.stringify(
                        {
                          userId,
                          current_session_id: currentSession?.session_id,
                          current_session_start: currentSession?.session_start,
                          active_session_id: session?.id,
                          active_session_start: session?.last_seen,
                          deviceType,
                        },
                        null,
                        2,
                      )}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse md:flex-row gap-3 items-center justify-end w-full">
            <Button variant="outline" color="neutral" onClick={onLogout} className="max-w-none">
              Return to Log In Page
            </Button>
            <Button
              variant="solid"
              color="primary"
              onClick={onStartSession}
              data-testid="log-out-other-session"
              className="max-w-none"
            >
              Log Out Other Session and Continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
